// The **Report** component displays gender pay gap data and related insights in a structured format.
// It presents key statistics, including hourly pay and bonus differences, using tables for clarity.
// Sections such as proportion, pay quartiles, understanding information, and retail insights are included
// to provide a comprehensive overview. The component utilizes **MarkDownDescription** to render formatted
// descriptions dynamically. Styled for responsiveness, it ensures a clean and accessible layout across devices.
//
import { MarkDownDescription } from 'components/commercetools-ui/atoms/markdown/markdown';
import React from 'react';
import { PieContent, ReportDataSource } from 'types/next';

interface ReportDataProps {
  reportData: ReportDataSource;
}

const Report = ({ reportData }: ReportDataProps) => {
  const gapReport = reportData?.gapReport || '';
  const propotion = reportData?.propotion || '';
  const payQuartiles = reportData?.payQuartiles || '';
  const understandingInfo = reportData?.understandingInfo || '';
  const retailInfo = reportData?.retailInfo || '';
  return (
    <div className="m-30 mx-auto items-center justify-center p-20 pb-50 md:mx-40 md:px-20 lg:w-[1170px] lg:px-40 2xl:w-[73rem]">
      <div className="gender-hap-report-section flex min-h-screen w-full flex-col items-center justify-center gap-y-10 overflow-hidden px-4">
        <div className="mx-auto min-w-full max-w-screen-lg">
          <div className="flex flex-col justify-center">
            <h2 className="text-center text-32 font-normal leading-7">{gapReport?.overallTitle}</h2>
            <br></br>
            <p className="font-body text-16 font-normal leading-[19.2px]">{gapReport?.description}</p>
          </div>

          <div className="mb-6 flex w-full justify-center px-5 sm:px-10 md:mx-50 md:px-[50px] lg:px-[350px]">
            <div className="mx-auto w-full max-w-screen-lg md:overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr>
                    <th className="p-2  text-left  text-22 font-semibold sm:p-3 sm:text-[22px] lg:text-[22px]">
                      Gender Pay Gap
                    </th>
                    <th className="p-2 text-center  text-22 font-semibold  sm:p-3 sm:text-[22px] lg:text-[22px]">
                      Mean
                    </th>
                    <th className="p-2 text-center  text-22 font-semibold  sm:p-3 sm:text-[22px] lg:text-[22px]">
                      Median
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2 text-left  text-22 font-semibold sm:p-3 sm:text-[22px] lg:text-[22px]">
                      Hourly Pay
                    </td>
                    <td className="p-2  text-center  text-20 font-medium sm:p-3 sm:text-[20px] lg:text-[20px]">
                      {gapReport?.genderPayGap?.hourlyPay?.mean}%
                    </td>
                    <td className="p-2 text-center  text-20 font-medium sm:p-3 sm:text-[20px] lg:text-[20px]">
                      {gapReport?.genderPayGap?.hourlyPay?.median}%
                    </td>
                  </tr>
                  <tr>
                    <td className="p-2  text-left  text-22 font-semibold  sm:p-3 sm:text-[22px] lg:text-[22px]">
                      Bonus
                    </td>
                    <td className="p-2 text-center  text-20 font-medium sm:p-3 sm:text-[20px] lg:text-[20px]">
                      {gapReport?.genderPayGap?.bonus?.mean}%
                    </td>
                    <td className="p-2 text-center  text-20 font-medium sm:p-3 sm:text-[20px] lg:text-[20px]">
                      {gapReport?.genderPayGap?.bonus?.median}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr className="h-1 border-gray-300" />
          <div className="propotion-section my-32">
            <div className="flex  flex-col items-center justify-center ">
              <h1 className="text-center text-16 font-semibold normal-case leading-7">{propotion?.overallTitle}</h1>
            </div>
            <div className="flex w-auto items-center justify-center gap-8 overflow-hidden">
              {propotion?.maleImage?.defaultHost && propotion?.maleImage?.endpoint && propotion?.maleImage?.name && (
                <img
                  src={`https://${propotion?.maleImage?.defaultHost}/i/${propotion?.maleImage?.endpoint}/${propotion?.maleImage?.name}`}
                  className="max-w-[40%] object-contain sm:h-auto sm:max-w-[25%] md:max-w-[25%]  lg:max-w-[40%]"
                />
              )}

              {propotion?.femaleImage?.defaultHost &&
                propotion?.femaleImage?.endpoint &&
                propotion?.femaleImage?.name && (
                  <img
                    src={`https://americangolf.a.bigcontent.io/v1/static/${propotion?.femaleImage?.name}`}
                    className="max-w-[40%] object-contain sm:h-auto sm:max-w-[25%] md:max-w-[25%]  lg:max-w-[40%]"
                  />
                )}
            </div>
          </div>
          <hr className="h-1 border-gray-300" />
          <div className="payQuartiles-section">
            <div className="flex items-center justify-center pt-20">
              <h1 className="text-center text-32 font-normal normal-case leading-6">{payQuartiles?.overallTitle}</h1>
            </div>
            <div className="flex items-center justify-center pt-15">
              <h4 className="text-center text-16 font-semibold normal-case leading-6">
                {payQuartiles?.proportionInfo}
              </h4>
            </div>
            <br></br>
            <div className="grid grid-cols-2 items-center justify-items-center gap-8 p-15 md:grid-cols-4">
              {payQuartiles?.content?.map((content: PieContent, index: number) => {
                if (!content?.image?.defaultHost && !content?.image?.endpoint && !content?.image?.name) return;
                return (
                  <>
                    <div key={index}>
                      <img
                        src={`https://${content?.image?.defaultHost}/i/${content?.image?.endpoint}/${content?.image?.name}`}
                        className="h-auto w-auto "
                        alt="name"
                      />
                    </div>
                  </>
                );
              })}
            </div>
          </div>
          <hr className="h-1 border-gray-300" />
          <div className="understandinginfo my-20">
            <div className="mb-20 flex items-center justify-center">
              <h2 className="mb-10 text-center font-body text-32 font-normal leading-7">
                {understandingInfo?.overallTitle}
              </h2>
              <br></br>
            </div>
            <div className="mb-20 flex items-center justify-center text-16 ">
              <MarkDownDescription markdownText={understandingInfo?.description} classname="description" />
            </div>
          </div>
          <hr className="h-1 border-gray-300" />
          <div className="retailinfo my-20">
            <div className="flex items-center justify-center">
              <h2 className="mb-10 text-center font-body text-32 font-normal leading-7">{retailInfo?.overallTitle}</h2>
            </div>
            <br></br>
            <MarkDownDescription markdownText={retailInfo?.description} classname="description" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Report;
